import './AvatarEditorModal.scss';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import { ActionIcon, Button, Group, Modal, Slider, Stack, Text } from '@mantine/core';
import Cropper, { Area, MediaSize, Point } from 'react-easy-crop';
import { HiOutlineTrash } from 'react-icons/hi2';
import { MdAdd, MdRemove } from 'react-icons/md';
import InputFile from '../../atoms/InputFile';
import ButtonWithConfirm from '../../molecules/ButtonWithConfirm';
import ModalLoadingOverlay from '../../atoms/ModalLoadingOverlay';
import { DialogAria } from '../../atoms/Modal/DialogAria';
import { usePortalTarget } from '../../provider/UiContext';
import type { IFileData } from '../../types/IFileData';
import { getCroppedBlob } from './cropImage';
import { useModalCloseGuard } from '../../hooks/useModalCloseGuard';

export interface AvatarEditorModalProps {
    opened: boolean;
    onClose: () => void;
    /** Сохранение: получает готовый квадратный PNG-файл. */
    onSave: (file: File) => Promise<void> | void;
    /** Удаление текущего фото. */
    onRemove?: () => Promise<void> | void;
    /** Есть ли уже фото — показывать «Удалить фото». */
    hasExisting?: boolean;
    /** Текущее фото (data-URL) — превью в стартовом экране диалога. */
    currentSrc?: string;
    /** Подпись превью текущего фото для скринридера */
    currentAlt?: string;
    /** Скруглённый прямоугольник (для групп) вместо круга (для людей). */
    rounded?: boolean;
    header?: string;
    /** Изображение (data-URL), с которым диалог открывается сразу на обрезке: файл перетащили на фото или выбрали. */
    initialImage?: string;
}

const MAX_ZOOM = 3;

/**
 * Модальный редактор аватара: drag-n-drop загрузка, круглая (или скруглённая квадратная) маска обрезки, зум
 * (слайдер/колесо/пинч), перетаскивание фото и живые превью. На выходе — квадратный PNG (бэк дополнительно
 * нормализует). Переиспользуется из меню пользователя, профиля и карточек адресной книги админки.
 */
const AvatarEditorSession: React.FC<AvatarEditorModalProps> = ({
    opened,
    onClose,
    onSave,
    onRemove,
    hasExisting = false,
    currentSrc,
    currentAlt = 'Текущее фото профиля',
    rounded = false,
    header = 'Фото профиля',
    initialImage,
}) => {
    const portalTarget = usePortalTarget();
    const dialogId = useId();
    const operation = useRef<AbortController | null>(null);
    useEffect(
        () => () => {
            operation.current?.abort();
        },
        [],
    );
    const [imageSrc, setImageSrc] = useState<string | null>(initialImage ?? null);
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [minZoom, setMinZoom] = useState(1);
    const [areaPx, setAreaPx] = useState<Area | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const reset = useCallback(() => {
        setImageSrc(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setMinZoom(1);
        setAreaPx(null);
        setError(null);
    }, []);

    const handleClose = useCallback(() => {
        if (saving || operation.current) return;
        reset();
        onClose();
    }, [saving, reset, onClose]);

    const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
        setAreaPx(croppedAreaPixels);
    }, []);

    const onMediaLoaded = useCallback((mediaSize: MediaSize) => {
        const w = mediaSize.naturalWidth;
        const h = mediaSize.naturalHeight;
        if (!w || !h) return;
        if (w * h > 24_000_000) {
            setImageSrc(null);
            setAreaPx(null);
            setError('Изображение превышает 24 мегапикселя. Выберите файл меньшего разрешения.');
            return;
        }
        // zoom=1 — «cover»; разрешаем зум-аут так, чтобы фото целиком влезало в круг + запас
        const fit = Math.min(w, h) / Math.max(w, h);
        setMinZoom(Math.max(0.2, Math.min(1, fit * 0.8)));
    }, []);

    const handleFile = useCallback((files: IFileData[]) => {
        const f = files[0];
        if (f?.base64) {
            setError(null);
            setAreaPx(null);
            setMinZoom(1);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setImageSrc(f.base64);
        }
    }, []);

    const handleSave = useCallback(async () => {
        if (!imageSrc || !areaPx || operation.current) return;
        const controller = new AbortController();
        operation.current = controller;
        setSaving(true);
        setError(null);
        try {
            const blob = await getCroppedBlob(imageSrc, areaPx, 1024, 'image/png', controller.signal);
            const file = new File([blob], 'avatar.png', { type: blob.type || 'image/png' });
            if (controller.signal.aborted) return;
            await onSave(file);
            if (controller.signal.aborted) return;
            reset();
            onClose();
        } catch {
            if (controller.signal.aborted) return;
            setError('Не удалось сохранить фото. Попробуйте другое изображение.');
        } finally {
            operation.current = null;
            if (!controller.signal.aborted) setSaving(false);
        }
    }, [imageSrc, areaPx, onSave, reset, onClose]);

    const handleRemove = useCallback(async () => {
        if (!onRemove || operation.current) return;
        const controller = new AbortController();
        operation.current = controller;
        setSaving(true);
        setError(null);
        try {
            await onRemove();
            if (controller.signal.aborted) return;
            reset();
            onClose();
        } catch {
            if (controller.signal.aborted) return;
            setError('Не удалось удалить фото.');
        } finally {
            operation.current = null;
            if (!controller.signal.aborted) setSaving(false);
        }
    }, [onRemove, reset, onClose]);

    const explicitClose = useRef(false);
    const closeGuard = useModalCloseGuard({
        onClose: handleClose,
        closeDisabled: saving,
        hasUnsavedInput: () => Boolean(imageSrc),
    });

    return (
        <>
            <Modal
                id={dialogId}
                opened={opened}
                onClose={() => {
                    const reason = explicitClose.current ? 'close-button' : 'outside';
                    explicitClose.current = false;
                    closeGuard.requestClose(reason);
                }}
                closeButtonProps={{
                    'aria-label': 'Закрыть',
                    onClickCapture: () => {
                        explicitClose.current = true;
                    },
                }}
                closeOnEscape={!saving}
                closeOnClickOutside={!saving}
                title={header}
                size="lg"
                centered
                radius="md"
                portalProps={portalTarget ? { target: portalTarget } : undefined}
            >
                <ModalLoadingOverlay visible={saving}>
                    {!imageSrc ? (
                        <Stack gap="sm" className="ava-ed__drop">
                            {currentSrc && (
                                <div className="ava-ed__current">
                                    <img
                                        src={currentSrc}
                                        alt={currentAlt}
                                        className={clsx(
                                            'ava-ed__current-img',
                                            rounded && 'ava-ed__current-img--rounded',
                                        )}
                                    />
                                    <Text size="sm" c="dimmed">
                                        Текущее фото
                                    </Text>
                                </div>
                            )}
                            <InputFile
                                accept="image/*"
                                multiple={false}
                                count={1}
                                maxSize={5120}
                                onChange={handleFile}
                                onError={(e) => setError(e.message)}
                                notifyErrors={false}
                                placeholderDropzone="Перетащите фото сюда"
                                placeholderButton="Выберите файл с устройства"
                                showChips={false}
                            />
                            {error && (
                                <Text c="red" size="sm">
                                    {error}
                                </Text>
                            )}
                            {hasExisting && onRemove && (
                                <Group justify="flex-end">
                                    <ButtonWithConfirm
                                        mode="double"
                                        variant="outline-thin"
                                        tone="danger"
                                        size="sm"
                                        startAdornment={<HiOutlineTrash size={16} />}
                                        confirmationText="Удалить фото?"
                                        confirmationHint="Фото удалится сразу"
                                        okButtonVariant="light"
                                        okButtonTone="danger"
                                        disabled={saving}
                                        onClick={() => void handleRemove()}
                                    >
                                        Удалить фото
                                    </ButtonWithConfirm>
                                </Group>
                            )}
                        </Stack>
                    ) : (
                        <Stack gap="md">
                            <div className="ava-ed__studio">
                                <div className="ava-ed__viewport">
                                    <Cropper
                                        image={imageSrc}
                                        crop={crop}
                                        zoom={zoom}
                                        minZoom={minZoom}
                                        maxZoom={MAX_ZOOM}
                                        aspect={1}
                                        cropShape={rounded ? 'rect' : 'round'}
                                        showGrid={false}
                                        restrictPosition={false}
                                        style={rounded ? { cropAreaStyle: { borderRadius: '16%' } } : undefined}
                                        onCropChange={setCrop}
                                        onZoomChange={setZoom}
                                        onCropComplete={onCropComplete}
                                        onMediaLoaded={onMediaLoaded}
                                    />
                                </div>
                            </div>

                            <Group gap="sm" wrap="nowrap" className="ava-ed__zoom">
                                <ActionIcon
                                    variant="default"
                                    onClick={() => setZoom((z) => Math.max(minZoom, z - 0.2))}
                                    aria-label="Уменьшить"
                                >
                                    <MdRemove />
                                </ActionIcon>
                                <Slider
                                    style={{ flex: 1 }}
                                    min={minZoom}
                                    max={MAX_ZOOM}
                                    step={0.01}
                                    value={zoom}
                                    onChange={setZoom}
                                    label={null}
                                    aria-label="Масштаб"
                                />
                                <ActionIcon
                                    variant="default"
                                    onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 0.2))}
                                    aria-label="Увеличить"
                                >
                                    <MdAdd />
                                </ActionIcon>
                            </Group>

                            {error && (
                                <Text c="red" size="sm">
                                    {error}
                                </Text>
                            )}

                            <Group justify="space-between" wrap="nowrap">
                                <Button variant="subtle" color="gray" onClick={reset} disabled={saving}>
                                    Заменить
                                </Button>
                                <Group gap="sm" wrap="nowrap">
                                    <Button variant="default" onClick={handleClose} disabled={saving}>
                                        Отмена
                                    </Button>
                                    <Button onClick={() => void handleSave()} disabled={saving}>
                                        Сохранить
                                    </Button>
                                </Group>
                            </Group>
                        </Stack>
                    )}
                </ModalLoadingOverlay>
                <DialogAria rootId={dialogId} />
            </Modal>
            {closeGuard.confirmDialog}
        </>
    );
};

// Закрытый редактор не удерживает исходный data-URL и декодированное изображение.
const AvatarEditorModal: React.FC<AvatarEditorModalProps> = (props) =>
    props.opened ? <AvatarEditorSession {...props} /> : null;

export default AvatarEditorModal;
